import { css } from "@emotion/css";
import { PositionRoleEnum } from "@sinch/core/entities/serverEnums";
import { usePositionRole } from "@sinch/components/application/PositionRole/PositionRole";

const CounterChipClass = css`
  border-radius: 3px !important;
  background-color: #fff;
  padding: 0 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 14px;
  width: fit-content;
  font-weight: 400;
`;

const AttendanceCounterRootClass = css`
  display: flex;
  align-items: center;
  height: 100%;
  width: fit-content;
  flex-direction: row;
  gap: 2px;
`;

// noinspection CssUnusedSymbol -- fulfilment-* selectors are applied dynamically via classList.add
const AttendanceCountPartClass = css`
  font-size: 11px; /* Assuming 11px as the base font size, adjust as necessary */
  line-height: 1;
  color: #f36421;
  &.fulfilment-positive {
    color: #d9534f; /* Replace with your actual error color */
  }
  &.fulfilment-zero {
    color: #5cb85c; /* Replace with your actual success color */
  }
  &.fulfilment-negative {
    color: #0288d1; /* Replace with your actual info color */
  }
  &:empty {
    display: none;
  }
`;

const VerticalDividerClass = css`
  width: 1px;
  background-color: #e0e0e0; /* Default MUI divider color, adjust as needed */
  margin-left: auto; /* For flexItem effect */
  margin-right: auto; /* For flexItem effect */
  align-self: stretch;
  height: auto;
`;

const HideSiblingEmptyClass = css`
  &:has(+ *:empty) {
    display: none;
  }
`;

function getFulfilmentClassName(fulfilment: number | null | undefined) {
  const number = fulfilment ?? 0;
  if (number > 0) {
    return "fulfilment-positive";
  } else if (number === 0) {
    return "fulfilment-zero";
  } else {
    return "fulfilment-negative";
  }
}

const rootClass = "SinchPositionCell-fulfilment-chip";

export const fulfilmentChipTemplate = (() => `
            <div class="${CounterChipClass} ${rootClass}">
              <div class="${AttendanceCounterRootClass}">
                <span class="${AttendanceCountPartClass}"></span>
                <div class="${HideSiblingEmptyClass} ${VerticalDividerClass}"></div>
                <span class="${AttendanceCountPartClass}"></span>
                <div class="${HideSiblingEmptyClass} ${VerticalDividerClass}"></div>
                <span class="${AttendanceCountPartClass}"></span>
              </div>
            </div>`)();

export const useNativeFulfilmentChipTemplate = () => {
  const { getRoleAbbreviation } = usePositionRole();

  const fillData = <TEl extends HTMLElement = HTMLElement>(
    el: TEl,
    data: {
      attendanceSlots?: number;
      attendance?: number;
      attendancePending?: number;
      standby?: number;
      standbySlots?: number;
    }
  ) => {
    const { attendance = 0, attendanceSlots = 0, attendancePending = 0, standby = 0, standbySlots = 0 } = data;

    const capacityFulfilment = el.firstElementChild;

    const attendanceFulfilment = capacityFulfilment!.children[0];
    attendanceFulfilment.textContent = `${attendance ?? 0}/${attendanceSlots ?? 0}`;
    attendanceFulfilment.classList.add(getFulfilmentClassName(attendanceSlots - attendance));

    if (attendancePending) {
      const pendingFulfilment = capacityFulfilment!.children[2];
      pendingFulfilment.textContent = `${attendancePending}?`;
    }

    if (standbySlots && standby) {
      const standbyFulfilment = capacityFulfilment!.children[4];
      standbyFulfilment.textContent = `${standby}/${standbySlots} ${getRoleAbbreviation(PositionRoleEnum.Standby)}`;
      standbyFulfilment.classList.add(getFulfilmentClassName(standbySlots - standby));
    }
  };

  return { fillData, template: fulfilmentChipTemplate, rootClass };
};
